"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { usePathname } from "@/i18n/routing";

export type SearchModalConfig = {
	popularTopics: string[];
};

type SearchContextValue = {
	query: string;
	setQuery: (next: string) => void;
	isOpen: boolean;
	open: () => void;
	close: () => void;
	toggle: () => void;
	config: SearchModalConfig;
};

const SearchContext = createContext<SearchContextValue | null>(null);

/**
 * Provider for the search modal. Holds query + open state in local React state
 * (not URL) so reloads start with the modal closed — opening it has no
 * shareable semantic value, unlike cart/geo/mobile-menu modals which use
 * `useModal` URL state.
 */
export const SearchProvider = ({
	children,
	config,
}: {
	children: React.ReactNode;
	config: SearchModalConfig;
}) => {
	const [query, setQuery] = useState("");
	const [isOpen, setIsOpen] = useState(false);

	const open = useCallback(() => setIsOpen(true), []);
	const close = useCallback(() => setIsOpen(false), []);
	const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

	// Auto-close the modal on route changes. Tracks the previous pathname via a
	// ref so we don't close on mount (only when the path actually transitions).
	const pathname = usePathname();
	const prevPathRef = useRef<string | null>(null);
	useEffect(() => {
		if (prevPathRef.current !== null && prevPathRef.current !== pathname) {
			setIsOpen(false);
		}
		prevPathRef.current = pathname;
	}, [pathname]);

	return (
		<SearchContext.Provider value={{ query, setQuery, isOpen, open, close, toggle, config }}>
			{children}
		</SearchContext.Provider>
	);
};

const useSearchContext = () => {
	const ctx = useContext(SearchContext);
	if (!ctx) throw new Error("useSearch hooks must be used within <SearchProvider>");
	return ctx;
};

export const useSearchQuery = () => {
	const { query, setQuery } = useSearchContext();
	return { query, setQuery };
};

export const useSearchModalConfig = (): SearchModalConfig => useSearchContext().config;

export const useSearchModal = () => {
	const { isOpen, open, close, toggle } = useSearchContext();
	return { isOpen, open, close, toggle };
};
